import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

export default function Shop() {
  const { t } = useLang();
  const CATEGORIES = [
    { key: "All", label: t("shop.filterAll") },
    { key: "Batteries", label: t("shop.filterBatteries") },
    { key: "Parts", label: t("shop.filterParts") },
    { key: "Accessories", label: t("shop.filterAccessories") },
  ];
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const active = params.get("category") || "All";
  const urlQuery = params.get("q") || "";
  const [query, setQuery] = useState(urlQuery);
  const debounceRef = useRef(null);
  const paramsRef = useRef(params);

  // Keep a live ref of the latest URL params so the debounce timer below
  // never fires against a stale snapshot (e.g. a category change made while
  // the user is still typing must not get dropped).
  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  // Debounce query -> URL param
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const next = new URLSearchParams(paramsRef.current);
      if (query.trim()) next.set("q", query.trim());
      else next.delete("q");
      setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, setParams]);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (active !== "All") q.category = active;
    if (urlQuery) q.q = urlQuery;
    api.get("/products", { params: q })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [active, urlQuery]);

  const setCat = (c) => {
    const next = new URLSearchParams(params);
    if (c === "All") next.delete("category");
    else next.set("category", c);
    setParams(next);
  };

  const results = useMemo(() => products, [products]);

  return (
    <main data-testid="shop-page" className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <div className="mb-10">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{t("shop.badge")}</div>
        <h1 className="mt-2 font-display font-extrabold text-4xl sm:text-5xl tracking-tighter text-zinc-900">{t("shop.title")}</h1>
        <p className="mt-3 text-zinc-600 max-w-xl">{t("shop.subtitle")}</p>
      </div>

      <div className="mb-6">
        <div className="relative max-w-xl">
          <Search className="w-4 h-4 text-zinc-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            data-testid="shop-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("shop.searchPlaceholder")}
            className="pl-11 pr-10 h-12 rounded-full bg-white border-zinc-200 focus-visible:ring-brand"
          />
          {query && (
            <button
              data-testid="shop-search-clear"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-zinc-100 flex items-center justify-center"
              aria-label={t("shop.clearSearch")}
            >
              <X className="w-4 h-4 text-zinc-500" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-10">
        {CATEGORIES.map((c) => (
          <Button
            key={c.key}
            data-testid={`filter-${c.key.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setCat(c.key)}
            variant="ghost"
            className={`rounded-full h-9 px-4 text-sm font-semibold border ${active === c.key ? "bg-zinc-900 text-white border-zinc-900 hover:bg-zinc-900" : "bg-white text-zinc-800 border-zinc-200 hover:border-zinc-400"}`}
          >
            {c.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">{t("shop.loading")}</div>
      ) : results.length === 0 ? (
        <div className="text-center py-20">
          <div className="font-display font-bold text-lg text-zinc-900">{t("shop.noResults")}</div>
          <div className="text-sm text-zinc-500 mt-1">
            {urlQuery ? <>{t("shop.noMatch")} &ldquo;<span className="font-medium text-zinc-800">{urlQuery}</span>&rdquo;.</> : t("shop.tryDifferent")}
          </div>
        </div>
      ) : (
        <>
          {urlQuery && (
            <div data-testid="shop-search-count" className="mb-4 text-sm text-zinc-500">
              {results.length} {t("shop.resultsFor")} &ldquo;<span className="text-zinc-900 font-medium">{urlQuery}</span>&rdquo;
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {results.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </>
      )}
    </main>
  );
}
